import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  MenuItem,
  // Remove unused import
  // IconButton,
  Divider,
  Alert,
  CircularProgress,
  LinearProgress,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
});

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function BatchAddItemsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [results, setResults] = useState([]);
  const [returnPath, setReturnPath] = useState('/inventory');

  // Common units of measure for dropdown
  const commonUnits = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'g', label: 'Grams' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'ml', label: 'Milliliters' },
    { value: 'l', label: 'Liters' },
    { value: 'oz', label: 'Ounces' },
    { value: 'lb', label: 'Pounds' }
  ];

  // Common categories
  const commonCategories = [
    'Dairy',
    'Meat',
    'Produce',
    'Bakery',
    'Canned Goods',
    'Dry Goods',
    'Frozen Foods',
    'Beverages',
    'Snacks',
    'Condiments',
    'Household',
    'Personal Care',
    'Other'
  ];

  useEffect(() => {
    // Get products from location state
    if (location.state?.products) {
      setProducts(location.state.products);
    }
    
    // Get return path if provided
    if (location.state?.returnPath) {
      setReturnPath(location.state.returnPath);
    }
  }, [location]);

  const handleEditProduct = (index) => {
    setEditingIndex(index);
    setEditedProduct({ ...products[index] });
  };

  const handleSaveEdit = () => {
    if (editedProduct && editingIndex !== null) {
      const updatedProducts = [...products];
      updatedProducts[editingIndex] = editedProduct;
      setProducts(updatedProducts);
      setEditingIndex(null);
      setEditedProduct(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedProduct(null);
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...products];
    updatedProducts.splice(index, 1);
    setProducts(updatedProducts);
  };

  const handleInputChange = (field, value) => {
    setEditedProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAllToInventory = async () => {
    if (products.length === 0) {
      setNotification({
        type: 'warning',
        message: 'No products to add'
      });
      return;
    }

    setIsLoading(true);
    setActiveStep(1);
    const newResults = [];

    try {
      // Process each product sequentially
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        
        try {
          const response = await API.post('/api/inventory', product);
          newResults.push({
            product: product,
            success: true,
            message: 'Added successfully',
            data: response.data
          });
        } catch (error) {
          console.error(`Error adding product ${i}:`, error);
          newResults.push({
            product: product,
            success: false,
            message: error.response?.data?.message || 'Failed to add product'
          });
        }
      }

      setResults(newResults);
      setActiveStep(2);
      
      // Show success notification
      const successCount = newResults.filter(r => r.success).length;
      setNotification({
        type: successCount === products.length ? 'success' : 'info',
        message: `Added ${successCount} of ${products.length} products to inventory`
      });
      
    } catch (error) {
      console.error('Error in batch processing:', error);
      setNotification({
        type: 'error',
        message: 'An error occurred during batch processing'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = () => {
    navigate(returnPath);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      navigate(returnPath);
    }
  };

  return (
    <Container maxWidth="md">
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.type || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>

      {/* Edit Product Dialog */}
      <Dialog
        open={editingIndex !== null}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        {editedProduct && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6">Edit Product</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Product Name"
                    fullWidth
                    value={editedProduct.item_name || ''}
                    onChange={(e) => handleInputChange('item_name', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Brand"
                    fullWidth
                    value={editedProduct.brand || ''}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Category"
                    fullWidth
                    value={editedProduct.category || ''}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    {commonCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Quantity"
                    type="number"
                    fullWidth
                    value={editedProduct.quantity || 1}
                    onChange={(e) => handleInputChange('quantity', Math.max(1, parseInt(e.target.value, 10) || 1))}
                    InputProps={{
                      inputProps: { min: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Unit"
                    fullWidth
                    value={editedProduct.unit_of_measure || 'pcs'}
                    onChange={(e) => handleInputChange('unit_of_measure', e.target.value)}
                  >
                    {commonUnits.map((unit) => (
                      <MenuItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    multiline
                    rows={3}
                    fullWidth
                    value={editedProduct.notes || ''}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelEdit} color="inherit">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                color="primary"
                variant="contained"
                startIcon={<SaveIcon />}
              >
                Save Changes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon
              sx={{
                fontSize: 36,
                mr: 2,
                color: 'primary.main'
              }}
            />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Batch Add to Inventory
            </Typography>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Add multiple products to your inventory at once
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Review Products</StepLabel>
            <StepContent>
              {products.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No products to add. Please go back and select products.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Review and edit the products before adding them to your inventory.
                  </Typography>
                  
                  <List>
                    {products.map((product, index) => (
                      <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="h6" gutterBottom>
                                {product.item_name || 'Unnamed Product'}
                              </Typography>
                              {product.brand && (
                                <Typography variant="body2" color="text.secondary">
                                  Brand: {product.brand}
                                </Typography>
                              )}
                              {product.category && (
                                <Typography variant="body2" color="text.secondary">
                                  Category: {product.category}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', height: '100%', justifyContent: 'space-between' }}>
                                <Typography variant="body1">
                                  Quantity: {product.quantity || 1} {product.unit_of_measure || 'pcs'}
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                  {product.category && (
                                    <Chip
                                      label={product.category}
                                      color="primary"
                                      variant="outlined"
                                      size="small"
                                      sx={{ mr: 1, mb: 1 }}
                                    />
                                  )}
                                  {product.brand && (
                                    <Chip
                                      label={product.brand}
                                      color="secondary"
                                      variant="outlined"
                                      size="small"
                                      sx={{ mr: 1, mb: 1 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ justifyContent: 'flex-end' }}>
                          <Button
                            startIcon={<EditIcon />}
                            onClick={() => handleEditProduct(index)}
                            disabled={isLoading}
                          >
                            Edit
                          </Button>
                          <Button
                            startIcon={<DeleteIcon />}
                            color="error"
                            onClick={() => handleRemoveProduct(index)}
                            disabled={isLoading}
                          >
                            Remove
                          </Button>
                        </CardActions>
                      </Card>
                    ))}
                  </List>
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon />}
                      disabled={isLoading}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddAllToInventory}
                      disabled={products.length === 0 || isLoading}
                      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                    >
                      {isLoading ? 'Adding...' : 'Add All to Inventory'}
                    </Button>
                  </Box>
                </>
              )}
            </StepContent>
          </Step>
          
          <Step>
            <StepLabel>Processing</StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Adding products to inventory...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Please wait while we process your request.
                </Typography>
                {isLoading && (
                  <LinearProgress sx={{ width: '80%', mt: 3 }} />
                )}
              </Box>
            </StepContent>
          </Step>
          
          <Step>
            <StepLabel>Results</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Results of adding products to your inventory:
              </Typography>
              
              <List>
                {results.map((result, index) => (
                  <ListItem 
                    key={index}
                    divider={index < results.length - 1}
                    secondaryAction={
                      result.success ? 
                        <CheckCircleIcon color="success" /> : 
                        <ErrorIcon color="error" />
                    }
                  >
                    <ListItemText
                      primary={result.product.item_name}
                      secondary={result.message}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFinish}
                  disabled={isLoading}
                >
                  Finish
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>
    </Container>
  );
}

export default BatchAddItemsPage;